import {
  addMinutes,
  compareAsc,
  format,
  getDate,
  getMonth,
  getYear,
  isSameDay,
  parseISO,
  startOfDay,
  startOfToday
} from 'date-fns'
import { TimeService } from 'modules/login/helpers/token/token'

import { trimMultiSpaces } from './string'

interface IOptions {
  divider: string
  isWithDate: boolean
  isWithTime: boolean
}

const DEFAULT_OPTIONS = {
  isWithDate: true,
  isWithTime: false,
  divider: '–'
}

export const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

interface IDateObj {
  day: number | string
  month: string
  time: string
  timePretext: string
  year: number | string
}

interface IData {
  divider: string
  endDateObj: IDateObj | undefined
  isSingleDay: boolean
  isWithDate: boolean
  isWithTime: boolean
  isWithoutYears: boolean
  startDateObj: IDateObj | undefined
}

const DEFAULT_DATA = {
  startDateObj: {} as IDateObj | undefined,
  endDateObj: {} as IDateObj | undefined,
  divider: '–',
  isWithDate: true,
  isWithTime: true,
  isSingleDay: false,
  isWithoutYears: false
}

// Проверка равенства дат до дня включительно
const isOneDayRange = (data: IData) => {
  if (!data.startDateObj || !data.endDateObj) {
    return false
  }
  const { year: yearS, month: monthS, day: dayS } = data.startDateObj
  const { year: yearE, month: monthE, day: dayE } = data.endDateObj

  return yearS === yearE && monthS === monthE && dayS === dayE
}

// Преобразование даты из строки в объект
export const stringDateToObj = (date?: string, data: IData = DEFAULT_DATA): IDateObj | undefined => {
  if (!date) {
    return
  }

  const d = new Date(date)
  const day = data.isWithDate ? getDate(d) : ''
  const month = data.isWithDate ? months[getMonth(d)] : ''
  const year = !data.isWithDate || data.isWithoutYears ? '' : getYear(d)
  const time = data.isWithTime ? format(d, 'HH:mm') : ''
  const timePretext = year && time ? 'в' : ''

  return {
    day,
    month,
    year,
    timePretext,
    time
  }
}

// Проверка порядка дат
const isSwitchDates = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) {
    return false
  }

  return new Date(startDate).getTime() > new Date(endDate).getTime()
}

// Проверка на необходимость указания года в дате
const isPresentYears = (dateNow: number, ...dates: Array<string | undefined>) => {
  if (!dates.length) {
    return false
  }

  // const timeService = TimeService.getInstance();
  return !dates.filter(Boolean).some((date) => getYear(new Date(date as string)) !== getYear(new Date(dateNow)))
}

// Установка данных для форматтера
const setInitialData = (options: IOptions, data: IData, dateNow: number, startDate?: string, endDate?: string) => {
  data.isWithDate = options.isWithDate
  data.isWithTime = options.isWithTime
  data.isWithoutYears = isPresentYears(dateNow, startDate, endDate)

  if (isSwitchDates(startDate, endDate)) {
    data.startDateObj = stringDateToObj(endDate)
    data.endDateObj = stringDateToObj(startDate)
  } else {
    data.startDateObj = stringDateToObj(startDate)
    data.endDateObj = stringDateToObj(endDate)
  }

  data.isSingleDay = isOneDayRange(data)
  data.divider = options.divider
}

// Проверка на вывод только одной даты
const isPresentDateSingle = (data: IData, startDate?: string, endDate?: string) =>
  !startDate || !endDate || startDate === endDate || (isOneDayRange(data) && !data.isWithTime)

// Формирование строки с одной датой
const presentDateSingle = (data: IData) => {
  const dateObj = data.startDateObj || data.endDateObj

  if (!dateObj) {
    return ''
  }

  const { year, month, day, timePretext, time } = dateObj

  return trimMultiSpaces(`${day} ${month} ${year} ${timePretext} ${time}`)
}

// Формирование строки с периодом дат
const presentDateRange = (data: IData) => {
  const { year: yearS, month: monthS, day: dayS, timePretext: timePretextS, time: timeS } = data.startDateObj!
  const { year: yearE, month: monthE, day: dayE, timePretext: timePretextE, time: timeE } = data.endDateObj!

  const startDateString = `${dayS} ${monthS} ${yearS} ${timePretextS} ${timeS}`
  const endDateString = data.isSingleDay ? `${timeE}` : `${dayE} ${monthE} ${yearE} ${timePretextE} ${timeE}`

  return trimMultiSpaces(`${startDateString.trimEnd()} ${data.divider} ${endDateString.trimStart()}`)
}

// Форматирование промежутка дат
export const formatDate = (
  startDate?: string,
  endDate?: string,
  dateNow?: number,
  options: IOptions = DEFAULT_OPTIONS,
  data: IData = DEFAULT_DATA
) => {
  const currentDateNow = dateNow !== undefined ? dateNow : new TimeService().now()

  setInitialData(options, data, currentDateNow, startDate, endDate)

  return isPresentDateSingle(data, startDate, endDate) ? presentDateSingle(data) : presentDateRange(data)
}

/**
 * Преобразует дату и время для отображения в Московском часовом поясе
 * @param dateUTC - входная дата в формате UTC
 * @param hasTime - флаг, показывающий отображать время или нет, по-умолчанию = false
 */
export const formatDateToMoscow = (dateUTC?: string, hasTime = false): string => {
  if (!dateUTC) {
    return ''
  }

  const date = new Date(dateUTC)
  // + 180 т.к. нужно отобразить время по Москве (UTC+03:00), т.е. делаем поправку на московское время
  const dateMoscow = addMinutes(date, date.getTimezoneOffset() + 180)
  const options = {
    ...DEFAULT_OPTIONS,
    isWithTime: hasTime
  }

  return formatDate(dateMoscow.toString(), undefined, undefined, options)
}

export const getCorrectDuration = (durationInHours?: number): string => {
  if (!durationInHours) {
    return '–'
  }

  const days = durationInHours / 24

  if (days.toFixed(0)) {
    return `${Math.ceil(days)} дн`
  }

  return `${durationInHours} ч`
}

export const isBeforeToday = (date: Date) => compareAsc(startOfDay(date), startOfToday()) < 0

export const isSameCalendarDay = (dateTimeString: string, isoDateString: string): boolean => {
  const dateTime = new Date(dateTimeString)
  const date = parseISO(isoDateString)

  return isSameDay(startOfDay(dateTime), startOfDay(date))
}
